import { inject } from '@angular/core';
import { CanMatchFn, Router, UrlSegment } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { identity } from 'lodash-es';
import { EMPTY, Observable, defer, from, merge, of } from 'rxjs';
import { catchError, filter, ignoreElements, map, switchMap, take, tap } from 'rxjs/operators';
import { LoadToggles, LOAD_TOGGLES_FAIL } from 'Src/ng2/store/actions/toggle-actions';
import { LoadCurrentUser, LOAD_CURRENT_USER_FAIL } from 'Src/ng2/store/actions/current-user-actions';
import { ClearState } from 'Src/ng2/store/actions/clear-state-actions';
import { getToggleLoadedStatus } from 'Src/ng2/store/selectors/toggles-selectors';
import { getCurrentUser, getCurrentUserLoadedStatus } from 'Src/ng2/store/selectors/current-user-selectors';
import { ToggleService } from 'Src/ng2/shared/services/toggle/toggle.service';
import { Auth } from 'Src/ng2/shared/auth/auth.service';
import { UrlPathService } from 'Src/ng2/shared/services/url-path-service/url-path.service';
import { SessionStorageService } from 'Src/ng2/shared/services/web-storage/session-storage/session-storage.service';
import { ENTITY_MODE, TEntityMode } from 'Src/ng2/shared/typings/types/entity-mode.types';
import { ENTITY_TYPE, TOpEntityType } from 'Src/ng2/shared/typings/types/entity-type.types';
import { IUser } from 'Src/ng2/shared/typings/interfaces/user.interface';

interface IHomeEntityContext {
  entityType?: string;
  mode?: string;
  entityId?: string;
  districtUrlName?: string;
}

// `canMatch` for the OP-dashboard variant of the `home` route; returning false falls through
// to the sibling tiles variant. Renders the OP dashboard when One Portal is enabled for this
// user/context (onePortalEnabled) and the user is not a single-entity school (single-entity
// school keeps the hybrid tiles homepage).
//
// canMatch runs during route recognition, before the canActivate-phase guards that populate
// the store (TogglesGuard, CurrentUserGuard). On a fresh load/reload/entity switch the toggle
// and current user would otherwise be missing, so we load both here before deciding — the
// rollout check (onePortalEnabled) reads the user's role and would throw on a null user.
export const renderOpHomeDashboardCanMatch: CanMatchFn = (): Observable<boolean> => {
  const store = inject(Store);
  const actions = inject(Actions);
  const toggleService = inject(ToggleService);
  const router = inject(Router);
  const auth = inject(Auth);
  const urlPathService = inject(UrlPathService);
  const sessionStorageService = inject(SessionStorageService);

  const context = getHomeEntityContext(router);

  return ensureDistrictContext(store, auth, urlPathService, sessionStorageService, context).pipe(
    switchMap(() => ensureTogglesLoaded(store, actions, toggleLoadSchoolId(context))),
    switchMap(() => ensureCurrentUserLoaded(store, actions, auth)),
    map(user => shouldRenderOpHomeDashboard(context, toggleService, user)),
    // Any unexpected failure (e.g. the current-user email lookup rejecting for an
    // unauthenticated session) falls through to tiles instead of erroring route recognition —
    // an errored canMatch would kill the navigation before the canActivate-phase auth
    // redirects ever run.
    catchError(() => of(false)),
  );
};

// canMatch also runs before CurrentDistrictGuard, so on a fresh load no district is in session
// storage and the toggle/user requests would go out without the district header the backend
// requires (they come back 401 and the decision would always take the failure fallback).
// Resolve the district from the URL and seed it before loading, mirroring CurrentDistrictGuard's
// compare-and-reset semantics: when session storage holds a DIFFERENT district than the URL
// (cross-district direct load), the store is cleared before seeding so the loads refetch under
// the URL's district and the decision is not made with the stale district's toggles. Because the
// seeded value comes from the same mapping CurrentDistrictGuard applies later, its own reset
// does not fire a second time. Resolution failures are swallowed: the loads then fail and the
// decision falls through to tiles.
function ensureDistrictContext (store: Store, auth: Auth, urlPathService: UrlPathService, sessionStorageService: SessionStorageService, context: IHomeEntityContext): Observable<void> {
  if (!context.districtUrlName) return of(undefined);

  const storedDistrictId = sessionStorageService.getItem('currentDistrict');
  // Fast path: resolve against the cached districts list (no network) — on warm navigations
  // the stored district matches the URL and nothing needs to happen.
  const cachedDistrictId = urlPathService.getDistrictIdFromDistrictUrlDisplayName(context.districtUrlName);
  if (storedDistrictId && storedDistrictId === cachedDistrictId) return of(undefined);

  return from(auth.getUserDistricts()).pipe(
    tap(districts => {
      const districtId = urlPathService.getDistrictIdFromDistrictUrlDisplayName(context.districtUrlName, districts);
      // Unresolvable district segment (CurrentDistrictGuard handles the not-found redirect)
      // or the session already matches: nothing to change.
      if (!districtId || districtId === storedDistrictId) return;

      if (storedDistrictId) store.dispatch(new ClearState());
      sessionStorageService.setItem('currentDistrict', districtId);
    }),
    map(() => undefined),
    catchError(() => of(undefined)),
  );
}

function shouldRenderOpHomeDashboard (context: IHomeEntityContext, toggleService: ToggleService, user: IUser): boolean {
  // A null user reaches here only if the user load failed; onePortalEnabled's rollout check
  // would dereference it, so fall through to tiles.
  if (!context.entityType || !user) return false;

  const entityType = context.entityType as TOpEntityType;
  const entityMode = toEntityMode(context.mode);
  const enabled = toggleService.onePortalEnabled({ entityType, entityMode, user });
  if (!enabled) return false;

  const isSingleEntitySchool = entityType === ENTITY_TYPE.SCHOOL &&
    context.mode === ENTITY_MODE.SINGLE_ENTITY_URL_PREFIX;
  return !isSingleEntitySchool;
}

function toEntityMode (mode?: string): TEntityMode | null {
  if (mode === ENTITY_MODE.SINGLE_ENTITY_URL_PREFIX) return ENTITY_MODE.SINGLE_ENTITY;
  if (mode === ENTITY_MODE.MULTI_ENTITY_URL_PREFIX) return ENTITY_MODE.MULTI_ENTITY;
  return null;
}

// Loads toggles if the store is empty, then resolves once they're ready. The reducer leaves
// `loaded` false on LOAD_TOGGLES_FAIL, so we resolve on that action too — otherwise a failed
// fetch would stall route recognition instead of falling through to tiles.
//
// The outcome streams must be subscribed BEFORE the load is dispatched: a request that dies
// in the interceptor chain fails synchronously inside store.dispatch, and an outcome
// subscribed after the dispatch misses the failure action and never settles. merge subscribes
// its sources in order, so the deferred dispatch runs last.
function ensureTogglesLoaded (store: Store, actions: Actions, currentSchoolId: string | null): Observable<boolean> {
  const loaded$ = store.pipe(select(getToggleLoadedStatus), filter(identity));
  const failed$ = actions.pipe(ofType(LOAD_TOGGLES_FAIL), map(() => false));

  return store.pipe(
    select(getToggleLoadedStatus),
    take(1),
    switchMap(loaded => {
      if (loaded) return of(true);
      const dispatch$ = defer(() => {
        store.dispatch(new LoadToggles({ currentSchoolId }));
        return EMPTY;
      });
      return merge(loaded$, failed$, dispatch$).pipe(take(1));
    }),
  );
}

// Loads the current user if absent, then resolves with it once ready, mirroring
// CurrentUserGuard. Like the toggle load it resolves on LOAD_CURRENT_USER_FAIL too, so a failed
// fetch falls through to tiles (with a null user) rather than stalling route recognition.
//
// Same subscribe-before-dispatch invariant as ensureTogglesLoaded.
function ensureCurrentUserLoaded (store: Store, actions: Actions, auth: Auth): Observable<IUser> {
  const loaded$ = store.pipe(select(getCurrentUserLoadedStatus), filter(identity));
  const failed$ = actions.pipe(ofType(LOAD_CURRENT_USER_FAIL), map(() => false));

  return store.pipe(
    select(getCurrentUserLoadedStatus),
    take(1),
    switchMap(loaded => {
      if (loaded) return of(true);
      const dispatch$ = defer(() => from(auth.getCurrentUserEmail()).pipe(
        tap((email: string) => store.dispatch(new LoadCurrentUser({ email }))),
        ignoreElements(),
      ));
      return merge(loaded$, failed$, dispatch$).pipe(take(1));
    }),
    switchMap(() => store.pipe(select(getCurrentUser), take(1))),
  );
}

// Mirrors UrlNavigationService.getEntityId (single-entity URLs carry an entityId, network URLs
// don't) so our LoadToggles payload matches TogglesGuard's and doesn't change the toggle set.
function toggleLoadSchoolId (context: IHomeEntityContext): string | null {
  return context.mode === ENTITY_MODE.SINGLE_ENTITY_URL_PREFIX ? context.entityId ?? null : null;
}

// canMatch only receives the segments local to this route node (`['home']`), so the full URL
// is read from the in-flight navigation to locate the entity-type/mode/id segments.
function getHomeEntityContext (router: Router): IHomeEntityContext {
  const navigation = router.getCurrentNavigation();
  const urlTree = navigation?.finalUrl ?? navigation?.extractedUrl ?? router.parseUrl(router.url);
  const segments: UrlSegment[] = urlTree.root.children.primary?.segments ?? [];

  const entityTypeIdx = segments.findIndex(s => s.path === ENTITY_TYPE.SCHOOL || s.path === ENTITY_TYPE.SHELTER);
  if (entityTypeIdx === -1) return {};

  return {
    entityType: segments[entityTypeIdx].path,
    mode: segments[entityTypeIdx + 1]?.path,
    entityId: segments[entityTypeIdx + 2]?.path,
    // The district route param precedes the entity type (`/:district/school/...`)
    districtUrlName: entityTypeIdx > 0 ? segments[entityTypeIdx - 1].path : undefined,
  };
}
